// ═══════════════════════════════════════════════════════════════════════════
// La CSF confirmada con el SAT, a través de su código QR
//
// El operador: "El QR también lo trae el PDF. El QR sería más fiable que el
// OCR de un pdf mal parseado o un pdf que no tiene texto y que es ilegible."
//
// Toda Constancia de Situación Fiscal lleva un QR que apunta a la página de
// validación del SAT:
//
//   https://siat.sat.gob.mx/app/qr/faces/pages/mobile/validadorqr.jsf
//     ?D1=10&D2=1&D3=<idCIF>_<RFC>
//
// Esa página contesta, en una tabla, la identidad, el domicilio fiscal VIGENTE
// y los regímenes VIGENTES. Tres ventajas sobre leer el texto del PDF:
//
//   · No depende de cómo pdf.js parta las líneas, que es lo que corrompió los
//     domicilios de 9 clientes (ver csf-extractor.js).
//   · Funciona aunque el PDF no tenga texto, sin OCR.
//   · Dice lo de HOY. Una CSF de hace seis meses puede traer un domicilio que
//     ya cambió; el SAT contesta el actual.
//
// Lo que NO trae: las actividades económicas. El detector de giro de una
// vinícola (actividades.js) sigue leyendo el texto del PDF.
//
// Este módulo es puro: interpreta el QR y el HTML. Quien trae el HTML es la
// función de borde `validar-csf`, porque el navegador no puede pedírselo al
// SAT directamente (CORS).
// ═══════════════════════════════════════════════════════════════════════════

import { SAT_REGIMEN_FISCAL } from "./catalogos-sat.js";

const RE_RFC = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;
const RE_CURP = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;
// idCIF: 11 dígitos en las constancias vistas. Se acepta un margen por si el
// SAT lo alarga; lo que no se acepta es nada que no sean dígitos.
const RE_D3 = /^(\d{8,15})_([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3})$/;

/**
 * Lee el texto de un QR y dice si es el de una CSF.
 *
 * Exige el host, la ruta y la forma del parámetro: lo que viaje al servidor es
 * solo `d3`, y el servidor arma la dirección por su cuenta. Así un QR
 * cualquiera no puede hacer que el servidor visite otra página.
 *
 * @returns {{ d3, idCIF, rfc } | null}
 */
export function idDeQrCsf(texto) {
  let u;
  try { u = new URL(String(texto || "").trim()); } catch { return null; }
  if (u.protocol !== "https:" || u.hostname !== "siat.sat.gob.mx") return null;
  if (!/\/validadorqr\.jsf$/.test(u.pathname)) return null;
  if (u.searchParams.get("D1") !== "10") return null;
  const d3 = (u.searchParams.get("D3") || "").toUpperCase();
  const m = d3.match(RE_D3);
  if (!m) return null;
  return { d3, idCIF: m[1], rfc: m[2] };
}

/** ¿Es un `d3` válido? El servidor hace la misma comprobación. */
export const d3Valido = (d3) => RE_D3.test(String(d3 || ""));

const quitarEtiquetas = (s) => s
  .replace(/<[^>]+>/g, " ")
  .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
  .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
  .replace(/\s+/g, " ").trim();

const normalizar = (s) => String(s || "").toLowerCase()
  .normalize("NFD").replace(/[̀-ͯ]/g, "")
  .replace(/[():.]/g, "").replace(/\s+/g, " ").trim();

/**
 * Los renglones etiqueta → valor de la página del validador, en orden.
 *
 * Se parte por RENGLÓN (<tr>), no por celda. Partir por celdas y emparejarlas
 * de dos en dos se descuadra con la primera celda vacía —un número interior
 * que no existe— y a partir de ahí cada etiqueta recibe el valor de la
 * siguiente. Pasó al probarlo contra la constancia de persona física.
 */
export function renglonesDeValidador(html) {
  const filas = [];
  for (const [, tr] of String(html || "").matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)) {
    const celdas = [...tr.matchAll(/<td[^>]*>([\s\S]*?)<\/td>/gi)].map(m => quitarEtiquetas(m[1]));
    if (celdas.length < 2) continue;
    const etiqueta = celdas[0].replace(/:\s*$/, "").trim();
    if (!etiqueta) continue;
    filas.push([normalizar(etiqueta), celdas[1]]);
  }
  return filas;
}

/** El régimen como lo escribe el SAT → su clave del catálogo. */
export function regimenDeTexto(texto) {
  const t = normalizar(texto);
  if (!t) return null;
  // El más LARGO que quepa: "Actividades Empresariales con Plataformas
  // Tecnológicas" no debe caer en otro más corto que también esté contenido.
  const candidatos = SAT_REGIMEN_FISCAL
    .filter(r => t.includes(normalizar(r.label)))
    .sort((a, b) => b.label.length - a.label.length);
  return candidatos[0] ? { code: candidatos[0].code, label: candidatos[0].label } : null;
}

/**
 * Los campos de la CSF según el SAT, con el MISMO contrato que
 * `extraerCamposDeLineas`, para que quien los use no tenga que distinguir.
 *
 * @param html     la página del validador
 * @param esperado el RFC que venía en el QR; el de la página debe coincidir
 */
export function camposDeValidadorSat(html, esperado = "") {
  const r = {
    rfc: "", curp: "", legalName: "", firstName: "", lastName1: "", lastName2: "",
    taxSystem: "", taxSystemLabel: "",
    domicilioFiscalZip: "", street: "", exterior: "", interior: "",
    colonia: "", city: "", state: "", cp: "",
    regimenes: [], situacion: "",
    _confidence: {}, _warnings: [],
  };
  const texto = quitarEtiquetas(String(html || ""));

  // ── El SAT no reconoce la constancia ────────────────────────────────────
  if (/no se le ha emitido/i.test(texto)) {
    r._warnings.push({ level: "critical",
      message: "El SAT dice que a ese RFC no se le ha emitido cédula de identificación fiscal. " +
               "El QR no corresponde a una constancia válida." });
    r._sinRespuesta = true;
    return r;
  }

  const rfcPagina = (texto.match(/El RFC:\s*([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3})/) || [])[1] || "";
  const filas = renglonesDeValidador(html);
  if (!filas.length || !rfcPagina) {
    r._warnings.push({ level: "warning",
      message: "La página del SAT no trajo los datos esperados. Se usaron los del PDF." });
    r._sinRespuesta = true;
    return r;
  }

  const v = (...claves) => {
    for (const c of claves) {
      const f = filas.find(([e, val]) => e === c && val);
      if (f) return f[1];
    }
    return "";
  };

  r.rfc = rfcPagina;
  r._confidence.rfc = "sat";
  if (esperado && esperado !== rfcPagina) {
    r._warnings.push({ level: "critical", field: "rfc",
      message: `El QR dice ${esperado} y el SAT contestó ${rfcPagina}.` });
  }

  const curp = v("curp");
  if (RE_CURP.test(curp)) { r.curp = curp; r._confidence.curp = "sat"; }

  // Identidad. Moral: la denominación SIN el régimen de capital, que es lo que
  // pide el CFDI 4.0. Física: nombre y apellidos.
  r.firstName = v("nombre");
  r.lastName1 = v("apellido paterno");
  r.lastName2 = v("apellido materno");
  r.legalName = v("denominacion o razon social") ||
                [r.firstName, r.lastName1, r.lastName2].filter(Boolean).join(" ");
  if (r.legalName) r._confidence.legalName = "sat";
  r.situacion = v("situacion del contribuyente");

  // Domicilio fiscal vigente.
  r.domicilioFiscalZip = (v("cp", "codigo postal").match(/\d{5}/) || [""])[0];
  r.cp = r.domicilioFiscalZip;
  const vialidad = v("nombre de la vialidad");
  r.exterior = v("numero exterior");
  r.interior = v("numero interior");
  r.colonia = v("colonia");
  r.city = v("municipio o delegacion");
  r.state = v("entidad federativa");
  r.street = [vialidad, r.exterior].filter(Boolean).join(" ");
  if (r.interior) r.street += " Int. " + r.interior;
  for (const k of ["domicilioFiscalZip", "street", "colonia", "city", "state"])
    if (r[k]) r._confidence[k] = "sat";

  // Regímenes vigentes: cada "Régimen:" es uno.
  for (const [e, val] of filas) {
    if (e !== "regimen") continue;
    const reg = regimenDeTexto(val);
    if (reg && !r.regimenes.some(x => x.code === reg.code)) r.regimenes.push(reg);
    else if (!reg) r._warnings.push({ level: "warning",
      message: `El SAT reporta el régimen «${val}», que no está en el catálogo de la app.` });
  }
  if (r.regimenes.length) {
    r.taxSystem = r.regimenes[0].code;
    r.taxSystemLabel = r.regimenes[0].label;
    r._confidence.taxSystem = "sat";
    if (r.regimenes.length > 1) r._warnings.push({ level: "info",
      message: `El contribuyente tiene ${r.regimenes.length} regímenes; se propuso ${r.taxSystemLabel}. Verifica cuál aplica a la facturación.` });
  }

  // Avisar, no bloquear: un contribuyente suspendido puede seguir comprando,
  // pero facturarle es otra conversación.
  // REACTIVADO es activo: así lo reporta el SAT a quien volvió del padrón
  // (visto en una constancia real).
  if (r.situacion && !["activo", "reactivado"].includes(normalizar(r.situacion))) {
    r._warnings.push({ level: "warning",
      message: `El SAT reporta al contribuyente como «${r.situacion}».` });
  }
  if (!r.domicilioFiscalZip)
    r._warnings.push({ level: "critical", message: "El SAT no devolvió el código postal fiscal." });
  return r;
}

const CAMPOS = ["rfc", "curp", "legalName", "firstName", "lastName1", "lastName2",
                "taxSystem", "taxSystemLabel", "domicilioFiscalZip", "street",
                "exterior", "interior", "colonia", "city", "state", "cp"];

/**
 * Une lo leído del PDF con lo que contestó el SAT. Manda el SAT en cada campo
 * que trae; el PDF llena los huecos y aporta su texto (las actividades).
 *
 * Las diferencias se AVISAN. La más grave es el RFC: si el texto del PDF dice
 * otro RFC que su propio QR, el documento fue alterado o es de otra persona.
 * Las del domicilio son normales: la constancia es de otra fecha y el SAT
 * contesta el domicilio de hoy.
 */
export function combinarConSat(pdf, sat) {
  if (!sat || sat._sinRespuesta) {
    return { ...pdf, _fuente: pdf._usedOcr ? "ocr" : "pdf",
             _warnings: [...(pdf._warnings || []), ...((sat?._warnings) || [])] };
  }
  const r = { ...pdf };
  for (const k of CAMPOS) if (sat[k]) r[k] = sat[k];
  if (sat.regimenes?.length) r.regimenes = sat.regimenes;
  r.situacion = sat.situacion;
  r._confidence = { ...(pdf._confidence || {}), ...sat._confidence };

  // Los avisos del PDF que el SAT ya resolvió sobran ("no se encontró el
  // código postal" cuando el SAT sí lo dio).
  const avisos = [...sat._warnings];
  const tienePdf = !!(pdf.rfc || pdf.legalName || pdf.domicilioFiscalZip);
  if (tienePdf && pdf.rfc && pdf.rfc !== sat.rfc) {
    avisos.push({ level: "critical", field: "rfc",
      message: `El texto del PDF dice ${pdf.rfc} y su propio QR dice ${sat.rfc}. ` +
               "El documento pudo ser alterado: verifícalo antes de usarlo." });
  }
  if (tienePdf && pdf.domicilioFiscalZip && pdf.domicilioFiscalZip !== sat.domicilioFiscalZip) {
    avisos.push({ level: "info", field: "domicilioFiscalZip",
      message: `El PDF dice CP ${pdf.domicilioFiscalZip}; el SAT hoy dice ${sat.domicilioFiscalZip}. ` +
               "Se usó el del SAT: la constancia es de otra fecha." });
  }
  r._warnings = avisos;
  r._fuente = "sat";
  r._noText = false;
  return r;
}
